import TableBody from '@mui/material/TableBody';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { IoFilter } from 'react-icons/io5';
import { TbCreditCard } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminTitle } from '../../components/AdminTitle';
import { Loading } from '../../components/Loading';
import ReactSelect from '../../components/ReactSelect';
import { StyledButton } from '../../components/StyledButton';
import { StyledInput } from '../../components/StyledInput';

import theme from '../../styles/themes/default';
import {
  ExportContainer,
  FiltersContainer,
  InfoCardsWrapper,
  MandatoryDebtsContainer,
  VehicleContainer,
} from './style';
import { PiArrowClockwise, PiArrowLineUpRight, PiMoney } from 'react-icons/pi';
import { LuAlignEndHorizontal } from 'react-icons/lu';
import InfoCard from './components/InfoCard';
import { IDebts, IMandatoryDebt, useDebtsInfo } from '../../hooks/useDebts';
import DebtsDataTable, { HeadCell } from './components/DebtsDataTable';
import ConfigureTableModal, { IItem } from './components/ConfigureTableModal';
import ConfiguredTableRow from './components/ConfiguredTableRow';
import AdvancedFiltersModal from './components/AdvancedFiltersModal';
import { usePayment } from '../../context/PaymentContext';
import Swal from 'sweetalert2';
import GenericModal from '../../components/GenericModal';
import { HiClock } from 'react-icons/hi';
import { IoIosArrowDroprightCircle } from 'react-icons/io';
import { set } from 'rsuite/esm/utils/dateUtils';

const headersInitialState = [
  { key: 'vehiclePlate', label: 'Placa', id: 'item-0' },
  { key: 'vehicleUF', label: 'UF', id: 'item-1' },
  { key: 'debt', label: 'Débito', id: 'item-2' },
  { key: 'description', label: 'Descrição', id: 'item-3' },
  { key: 'value', label: 'Valor', id: 'item-4' },
  { key: 'value', label: 'Débitos Obrigatórios', id: 'item-5' },
  { key: 'value', label: 'Total de Débitos', id: 'item-6' },
  { key: 'conductor', label: 'Condutor', id: 'item-7' },
  { key: 'isExpired', label: 'Status', id: 'item-8' },
  { key: 'updated_at', label: 'Atualizado em', id: 'item-9' },
  { key: 'deadline', label: 'Vencimento', id: 'item-10' },
];

const DebtsListing: FC = () => {
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [orderBy, setOrderBy] = useState('updated_at');
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [textToShow, setTextToShow] = useState<string | undefined>('');
  const [exportFilename, setExportFilename] = useState<string>('');
  const [text, setText] = useState<string | undefined>(undefined);
  const [totalMultas, setTotalMultas] = useState<number>(0);
  const [totalIPVA, setTotalIPVA] = useState<number>(0);
  const [totalLicenciamento, setTotalLicenciamento] = useState<number>(0);
  const [totalOutros, setTotalOutros] = useState<number>(0);
  const [mandatoryDebts, setMandatoryDebts] = useState<IMandatoryDebt[]>([]);

  const [openConfigureTableModal, setOpenConfigureTableModal] =
    useState<boolean>(false);
  const [openGlobalFiltersModal, setOpenGlobalFiltersModal] =
    useState<boolean>(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);
  const [openGeneratingExportModal, setOpenGeneratingExportModal] =
    useState<boolean>(false);
  const [openMandatoryDebtsModal, setOpenMandatoryDebtsModal] =
    useState<boolean>(false);

  const [tableColumnsConfig, setTableColumnsConfig] = useState<
    { id: string; content?: string; show: boolean }[]
  >([]);

  const [headers, setHeaders] =
    useState<({ key: string; label: string } | undefined)[]>(
      headersInitialState,
    );

  const [debtType, setSetDebtType] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [debtStatus, setDebtStatus] = useState<
    { value: string; label: string } | undefined
  >(undefined);

  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const [isChecked, setIsChecked] = useState<{ [key: string]: boolean }>({});
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [listDebts, setListDebts] = useState<IDebts[]>([]);
  const {
    selectedDebts,
    setSelectedDebts,
    handleGetConsult,
    handleConsultClean
  } = usePayment();

  const {
    data: dataList,
    isLoading,
    error,
    refetch,
  } = useDebtsInfo({
    duplicated: false,
    take: itemsPerPage,
    page,
    order,
  });

  useEffect(() => {
    const filteredDebts =
      dataList?.data.filter(
        (debt) => debt.debtsCode in isChecked && isChecked[debt.debtsCode],
      ) ?? [];
    setSelectedDebts(filteredDebts);
  }, [isChecked, dataList]);

  useEffect(() => {
    itemsPerPage > 10 && setPage(1);
    Number(dataList?.meta?.itemCount) < itemsPerPage && setPage(1);
  }, [dataList?.meta?.itemCount, itemsPerPage]);

  const statusOptions = [
    { value: 'PENDING', label: 'Pag. Pendente' },
    { value: 'EXPIRING', label: 'A Vencer' },
    { value: 'EXPIRED', label: 'Vencido' },
  ];

  const debtTypeOptions = [
    { value: 'IPVA', label: 'IPVA' },
    { value: 'LICENSING', label: 'Licenciamento' },
    { value: 'TRAFFIC_TICKET', label: 'Multa' },
  ];

  const totalDebtsData = useMemo(() => {
    const totalSelected =
      totalLicenciamento + totalIPVA + totalMultas + totalOutros;

    return [
      {
        titleCard: 'Licenciamento',
        totalValue: `R$ ${(totalLicenciamento || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'IPVA',
        totalValue: `R$ ${(totalIPVA || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Multas',
        totalValue: `R$ ${(totalMultas || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Outros',
        totalValue: `R$ ${(totalOutros || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
      {
        titleCard: 'Total',
        totalValue: `R$ ${(totalSelected || 0)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      },
    ];
  }, [totalIPVA, totalLicenciamento, totalMultas, totalOutros]);

  useEffect(() => {
    setTotalIPVA(Number(dataList?.total?.ipva ?? 0));
    setTotalMultas(Number(dataList?.total?.multas ?? 0));
    setTotalLicenciamento(Number(dataList?.total?.licenciamento ?? 0));
    setTotalOutros(Number(dataList?.total?.outros ?? 0));
  }, [
    dataList?.total?.ipva,
    dataList?.total?.licenciamento,
    dataList?.total?.multas,
    dataList?.total?.outros,
  ]);

  useEffect(() => {
    if (isChecked && Object.keys(isChecked).length > 0) {
      const selectedDebts = dataList?.data.filter(
        (debt) =>
          debt?.debtsCode?.toString() in isChecked &&
          isChecked[debt?.debtsCode?.toString()],
      );

      if (selectedDebts && selectedDebts?.length > 0) {
        let selectedDebtsAndMandatoryByType: IMandatoryDebt[] = []
        let allSelectedCodes: number[] = []


        selectedDebts.forEach(debt => {
          if (debt?.onCheck?.select && debt.onCheck.select.length > 0) {
            const mandatoryDebts = debt.onCheck.select

            mandatoryDebts.forEach(mandatory => {
              if (!allSelectedCodes.includes(mandatory.debtsCode)) {
                allSelectedCodes.push(mandatory.debtsCode)
                selectedDebtsAndMandatoryByType.push(mandatory)
              }
            })
          }
          const debtLikeMandatory = {
            additionalDescription: debt.additionalDescription ?? "",
            consultNumber: debt.consultNumber,
            debtsCode: debt.debtsCode,
            description: debt.description,
            price: debt.price,
            type: debt.type,
          }

          if (!allSelectedCodes.includes(debt.debtsCode)) {
            allSelectedCodes.push(debt.debtsCode)
            selectedDebtsAndMandatoryByType.push(debtLikeMandatory)
          }
        })
        
        const selectedMultas = selectedDebtsAndMandatoryByType.filter(
          (debt) => debt?.type === 'multas',
        );
        const selectedIPVA = selectedDebtsAndMandatoryByType.filter(
          (debt) => debt?.type === 'ipva',
        );
        const selectedLicenciamentos = selectedDebtsAndMandatoryByType.filter(
          (debt) => debt?.type === 'licenciamento',
        );
        const selectedOutros = selectedDebtsAndMandatoryByType.filter(
          (debt) => debt?.type === 'outros',
        );

        setTotalIPVA(sumSelectedDebtsWithMandatoryDebts(selectedIPVA) ?? 0);
        setTotalMultas(sumSelectedDebtsWithMandatoryDebts(selectedMultas) ?? 0);
        setTotalLicenciamento(
          sumSelectedDebtsWithMandatoryDebts(selectedLicenciamentos) ?? 0,
        );
        setTotalOutros(sumSelectedDebtsWithMandatoryDebts(selectedOutros) ?? 0);
      } else {
        setTotalIPVA(Number(dataList?.total?.ipva ?? 0));
        setTotalMultas(Number(dataList?.total?.multas ?? 0));
        setTotalLicenciamento(Number(dataList?.total?.licenciamento ?? 0));
        setTotalOutros(Number(dataList?.total?.outros ?? 0));
      }
    }
  }, [isChecked]);

  const handleChangePlateOrDriverValue = (event: any) => {
    const upperCaseValue = event.target.value;
    setTextToShow(upperCaseValue);

    let debounceTimer;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      setText(upperCaseValue.toUpperCase());
    }, 500);
  };

  const handleChangeExportFileName = (event: any) => {
    const upperCaseValue = event.target.value;
    setExportFilename(upperCaseValue);
  };

  useEffect(() => {
    refetch();
    setTableColumnsConfig(handleColumnsToInitialState());
  }, []);

  useEffect(() => {
    const newHeaders = tableColumnsConfig
      .filter((col) => col.show)
      .map((col) => headersInitialState.find((head) => head?.id == col.id));

    if (newHeaders && newHeaders.length > 0) {
      setHeaders(newHeaders);
    }
  }, [tableColumnsConfig]);

  useEffect(() => {
    const refetchNewVehicle =
      (location?.state && location?.state?.newVehicle) || null;
    if (refetchNewVehicle) {
      refetch();
    }
  }, [location.state, refetch]);

  function sumSelectedDebtsWithMandatoryDebts(debts: IMandatoryDebt[]) {
    return debts.reduce(
      (total, debt) => total + Number(debt?.price ?? 0),
      0,
    );
  }

  const handleSetAllSelected = () => {
    if (!isAllChecked && dataList?.data && dataList?.data?.length > 0) {
      const newIsChecked = dataList.data.reduce(
        (acc, each) => ({ ...acc, [each.debtsCode]: true }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(true);
    }
    if (isAllChecked && dataList?.data && dataList?.data?.length > 0) {
      const newIsChecked = dataList.data.reduce(
        (acc, each) => ({ ...acc, [each.debtsCode]: false }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(false);
    }
  };

  function handleColumnsToInitialState() {
    const persistedConfig: IItem[] = JSON.parse(
      localStorage.getItem('@Zignet:tabela-debitos')!,
    );

    if (persistedConfig && persistedConfig.length > 0) return persistedConfig;

    return headersInitialState?.map((col, index) => ({
      id: `item-${index}`,
      content: col?.label,
      show: true,
    }));
  }

  function validateRowsChecked(obj: { [key: string]: boolean }): boolean {
    return Object.values(obj).some((value) => value === true);
  }
  const handlePayButton = async () => {
    const keepDebts = true
    handleConsultClean(keepDebts)
    if (selectedDebts.length > 0) {
      handleGetConsult()
      navigate('/debts/payment');
    }
  };

  function handleExportFile() {
    if (!exportFilename || exportFilename.length < 3) {
      return Swal.fire({
        icon: 'error',
        title: 'Nome inválido!',
        html: `O nome do arquivo deve ter ao menos 3 caracteres`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    }

    console.log({ exportFilename });

    setOpenExportModal(false);
    setOpenGeneratingExportModal(true);
  }

  function handleMandatoryDebtsByType(mandatoryDebts: IMandatoryDebt[]) {
    const allTypes: string[] = [];
    mandatoryDebts.forEach((debt) => {
      if (!allTypes.includes(debt.type)) {
        allTypes.push(debt.type);
      }
    });

    return allTypes.map((type) => ({
      type,
      data: mandatoryDebts.filter((debt) => debt.type === type),
    }));
  }

  return (
    <VehicleContainer>
      {isLoading && <Loading />}

      <ConfigureTableModal
        show={openConfigureTableModal}
        setShow={setOpenConfigureTableModal}
        initialItems={handleColumnsToInitialState()}
        setTableColumns={setTableColumnsConfig}
      />

      <AdvancedFiltersModal
        open={openGlobalFiltersModal}
        setOpen={setOpenGlobalFiltersModal}
      />

      <GenericModal
        open={openExportModal}
        setOpen={setOpenExportModal}
        showActionsButtons
        rightButtonText="Exportar"
        leftButtonText="Voltar"
        title="Exportar relatório"
        text="Defina um nume para o relatório que deseja exportar"
        iconTitle={
          <PiArrowLineUpRight style={{ fontSize: 48, color: '#F58220' }} />
        }
        rightButtonAction={handleExportFile}
      >
        <ExportContainer>
          <StyledInput
            placeholder="Digite o nome do relatório"
            value={exportFilename}
            onChange={handleChangeExportFileName}
          />
        </ExportContainer>
      </GenericModal>

      <GenericModal
        open={openGeneratingExportModal}
        setOpen={setOpenGeneratingExportModal}
        showActionsButtons
        rightButtonText="Ir para exportações"
        leftButtonText="Voltar"
        title="Gerando relatório..."
        text="O relatório está sendo gerado, você será informado por e-mail quando o processo for finalizado."
        iconTitle={<HiClock style={{ fontSize: 48, color: '#F58220' }} />}
        rightButtonAction={() => navigate('/exports')}
        containerStyle={{ width: '450px', textAlign: 'center' }}
        actionsStyle={{ width: '400px' }}
      />

      <GenericModal
        open={openMandatoryDebtsModal}
        setOpen={setOpenMandatoryDebtsModal}
        showActionsButtons
        leftButtonStyle={{ display: 'none' }}
        rightButtonText={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                lineHeight: '21px',
                fontWeight: '500',
              }}
            >
              Fechar
            </span>
            <IoIosArrowDroprightCircle style={{ fontSize: '15px' }} />
          </div>
        }
        rightButtonAction={() => setOpenMandatoryDebtsModal(false)}
        rightButtonStyle={{ width: 'fit-content', padding: '14px 32px' }}
      >
        <MandatoryDebtsContainer>
          <span className="title-children">Débitos Obrigatórios</span>
          {handleMandatoryDebtsByType(mandatoryDebts).map((container) => (
            <div className="mandatory-debt">
              <span className="debt-type">{container.type?.toUpperCase()}</span>

              {container.data.map((debt) => (
                <div className="debt-desc">
                  <span>{debt.additionalDescription}</span>
                  <span>{`R$ ${(debt.price ?? 0)?.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}</span>
                </div>
              ))}
            </div>
          ))}
        </MandatoryDebtsContainer>
      </GenericModal>

      <AdminTitle
        title="Débitos Veículares"
        icon={<PiMoney style={{ fontSize: '30px' }} />}
      />

      <InfoCardsWrapper>
        {totalDebtsData.map((data, index) => (
          <InfoCard
            isLoading={isLoading}
            key={index + data.titleCard}
            titleCard={data.titleCard}
            totalValue={data.totalValue}
          />
        ))}
      </InfoCardsWrapper>

      <FiltersContainer>
        <StyledButton
          text="Atualizar Frota"
          textColor="white"
          backgroundColor="#0C9AD7"
          icon={<PiArrowClockwise style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={() => console.log('Atualizar frota')}
          disabled={!validateRowsChecked(isChecked)}
        />
        <StyledButton
          text="Exportar"
          textColor="white"
          backgroundColor="#005EAD"
          icon={<PiArrowLineUpRight style={{ fontSize: 26 }} />}
          iconPosition="left"
          disabled={!validateRowsChecked(isChecked)}
          onClick={() => setOpenExportModal(true)}
        />
      </FiltersContainer>

      <FiltersContainer>
        <label
          htmlFor=""
          className="inputSearch"
          style={{ gridColumn: 'span 2' }}
        >
          <StyledInput
            placeholder="Placa ou Condutor"
            value={textToShow}
            onChange={handleChangePlateOrDriverValue}
          />
          <span className="debt-filter">
            <FiSearch />
          </span>
        </label>
        <ReactSelect
          placeholder="Status"
          isLoading={false}
          allOptions={statusOptions}
          setSelectedOption={setDebtStatus}
          value={debtStatus}
          name="status"
          isClearable
        />
        <ReactSelect
          placeholder="Tipo de Débito"
          isLoading={false}
          allOptions={debtTypeOptions}
          setSelectedOption={setSetDebtType}
          value={debtType}
          name="UF"
          isClearable
        />
        <StyledButton
          text="Filtros Avançados"
          textColor="white"
          backgroundColor="#FAB040"
          icon={<IoFilter style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={() => setOpenGlobalFiltersModal(true)}
        />
        <StyledButton
          text="Configurar Tabela"
          textColor="#F58220"
          backgroundColor="white"
          border="1px solid #F58220"
          icon={
            <LuAlignEndHorizontal style={{ fontSize: 26, color: '#F58220' }} />
          }
          iconPosition="left"
          onClick={() => setOpenConfigureTableModal(true)}
        />
        <StyledButton
          text="Pagar"
          textColor={theme.colors.white}
          backgroundColor={theme.colors.orange.primary}
          icon={<TbCreditCard style={{ fontSize: 26 }} />}
          iconPosition="left"
          onClick={handlePayButton}
          disabled={!validateRowsChecked(isChecked)}
        />
      </FiltersContainer>

      <DebtsDataTable
        title="Listagem de Débitos"
        heads={headers as HeadCell[]}
        hasPagination
        data={dataList?.data}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        page={Number(dataList?.meta?.page)}
        setPage={setPage}
        itemsPerPage={itemsPerPage}
        nPages={dataList?.meta?.pageCount}
        isLoading={isLoading}
        error={error}
        totalItems={dataList?.meta?.itemCount}
        hasCheckbox={true}
        isChecked={isAllChecked}
        setIsChecked={handleSetAllSelected}
      >
        <TableBody>
          <ConfiguredTableRow
            data={dataList}
            configuredColumns={tableColumnsConfig}
            setIsChecked={setIsChecked}
            isChecked={isChecked}
            handleRowAction={setOpenMandatoryDebtsModal}
            setMandatoryDebts={setMandatoryDebts}
          />
        </TableBody>
      </DebtsDataTable>
    </VehicleContainer>
  );
};

export default DebtsListing;
